import React from 'react'
import Button from 'react-bootstrap/Button'
import * as H from 'history'
import { GrantAgreementModel } from '../../../models/GrantAgreementModel'
import { ISupportSpinner } from '../../../shared/Ajax'
import { Backdrop } from '../../../shared/Backdrop'
import {
    Checkbox, Loading, NumericSelectField, TextField, validateFields,
} from '../../../shared/fields'
import { Salutation } from '../../../shared/Utility'
import { ApplicationController } from '../../../controllers/ApplicationController'
import { DashboardModel } from '../../../models/DashboardModel'
import ApplicationSummary from './ApplicationSummary'

interface Props {
    id: string
    history: H.History
}

export interface State {
    loading: boolean
    grantAgreementModel: GrantAgreementModel
    model: DashboardModel
    showGivenNameError: boolean
    showFamilyNameError: boolean
    disableSubmit: boolean
}

export default class AcceptForm extends React.Component<Props, State> implements ISupportSpinner {
    declaration: Checkbox
    title: NumericSelectField
    givenName: TextField
    familyName: TextField
    position: TextField
    ajax: ApplicationController

    constructor(props: Props) {
        super(props)
        this.ajax = new ApplicationController()
        this.state = {
            loading: false,
            model: null,
            grantAgreementModel: null,
            showGivenNameError: null,
            showFamilyNameError: null,
            disableSubmit: false,
        }
    }

    componentDidMount() {
        const { id, history } = this.props
        this.ajax.getDashBoardDetailByApplicationId(this, id, (model: DashboardModel) => {
            const userAuthToView = model?.dashboardDetailModels[0]?.grantAgreementSummaryModel?.canAcceptOrDecline

            if (!userAuthToView) {
                history.push('/') // previosly '/dashboard-reform'
            }
            this.setState({
                model,
                grantAgreementModel: {
                    ...model?.dashboardDetailModels?.[0].grantAgreementModel,
                    claimRandomIdentifier: model.dashboardDetailModels[0].claimRandomIdentifier,
                },
            })
        }, (err) => {
            alert(err)
            history.push('/') // previously '/dashboard-reform'
        })
    }

    componentDidUpdate(prevProps: Readonly<Props>, prevState: Readonly<State>): void {
        const { grantAgreementModel } = this.state

        if (grantAgreementModel?.givenName !== prevState?.grantAgreementModel?.givenName) {
            this.setState({ showGivenNameError: false })
        }

        if (grantAgreementModel?.familyName !== prevState?.grantAgreementModel?.familyName) {
            this.setState({ showFamilyNameError: false })
        }
    }

    toggleSpinner(on: boolean, callback: { (): any }): void {
        this.setState({ loading: on }, callback)
    }

    onChange = (changes: Partial<GrantAgreementModel>, callback?: { (): any }) => {
        this.setState({ grantAgreementModel: { ...this.state.grantAgreementModel, ...changes } }, callback)
    }

    downloadAgreement() {
        const { model } = this.state
        const claimIdentifier = model?.dashboardDetailModels?.[0]?.claimRandomIdentifier
        window.open(`/api/DocumentReform/downloadAgreement?claimIdentifier=${claimIdentifier}`, '_blank')
    }

    validName() {
        const { grantAgreementModel, model } = this.state
        const modifiedName = !!model?.dashboardDetailModels[0]?.modifiedPrimaryContactName
        const nameEntered = `${grantAgreementModel?.givenName} ${grantAgreementModel?.familyName}`

        if (modifiedName) {
            return model?.dashboardDetailModels[0]?.modifiedPrimaryContactName?.toLowerCase().trim() !== nameEntered.toLowerCase().trim()
        }
        return model?.dashboardDetailModels[0]?.primaryContactName?.toLowerCase().trim() !== nameEntered.toLowerCase().trim()
    }

    canSubmit(): Promise<boolean> {
        return new Promise((resolve) => {
            const { model } = this.state
            const { id } = this.props
            this.ajax.getDashBoardDetailByApplicationId(this, id, (newModel: DashboardModel) => {
                const newGrantAgreementSummaryModel = newModel.dashboardDetailModels[0].grantAgreementSummaryModel
                const stateGrantAgreementSummaryModel = model.dashboardDetailModels[0].grantAgreementSummaryModel

                // Parse the dueDate
                const dueDate = new Date(newGrantAgreementSummaryModel.dueDate);
                const currentDate = new Date();

                // Check if the dueDate has elapsed
                if (dueDate < currentDate) {
                    resolve(false);
                    return;
                }


                resolve(Object.entries(stateGrantAgreementSummaryModel).sort().toString() === Object.entries(newGrantAgreementSummaryModel).sort().toString())
            })
        })
    }

    validate() {
        validateFields((res) => {
            this.setState({ showFamilyNameError: this.validName(), showGivenNameError: this.validName() }, async () => {
                if (!this.validName() && res) {
                    if (confirm('You are about to submit your acceptance of the grant agreement. Proceed? \n\n You can\'t make any changes once you submit your grant agreem​ent.')) {
                        const { grantAgreementModel } = this.state
                        const canSubmit = await this.canSubmit()
                        if (canSubmit) {
                            this.ajax.grantAgreementAccepted(this, grantAgreementModel,
                                (grantAgreementNumber: string) => this.setState({ grantAgreementModel: { ...grantAgreementModel, grantAgreementNumber } }))
                        } else {
                            alert('There has been an update to the grant agreement details. Please refresh the page')
                            this.setState({ disableSubmit: true })
                        }
                    }
                }
            })
        },
            this.declaration,
            this.title,
            this.givenName,
            this.familyName,
            this.position)
    }

    getErrorMessage(field: string) {
        const {
            grantAgreementModel, showGivenNameError, showFamilyNameError,
        } = this.state

        if (field === 'givenName' && showGivenNameError) {
            if (grantAgreementModel.givenName?.length > 0) {
                return 'The applicant Given Name or Family Name is incorrect. They must be the same as your primary contact.'
            }
        }

        if (field === 'familyName' && showFamilyNameError) {
            if (grantAgreementModel.familyName?.length > 0) {
                return 'The applicant Given Name or Family Name is incorrect. They must be the same as your primary contact.'
            }
        }
        return null
    }

    render() {
        const {
            grantAgreementModel, loading, model, disableSubmit,
        } = this.state
        const { history } = this.props

        if (!model) return <Loading />

        const applicationDetails = model?.dashboardDetailModels?.[0]
        const isDraft = applicationDetails.applicationStatus.toLowerCase() === 'draft'

        const salutationDataSource = [
            { value: Salutation.Mr, label: 'Mr' },
            { value: Salutation.Mrs, label: 'Mrs' },
            { value: Salutation.Ms, label: 'Ms' },
            { value: Salutation.Miss, label: 'Miss' },
            { value: Salutation.Other, label: 'Other' },
        ]

        if (grantAgreementModel?.grantAgreementNumber) {
            return (
                <div className='container-fluid pt-3'>
                    <h2>Accept Grant Agreement - Confirmation</h2>
                    <br />
                    <ApplicationSummary
                        isConsultant={model.isConsultant}
                        isDraft={isDraft}
                        model={model}
                        applicationDetails={applicationDetails}
                        showGrantAgreement
                        grantAgreement={applicationDetails.grantAgreementSummaryModel}
                    />
                    <p>
                        <div>{`Your Grant Agreement Id is: ${grantAgreementModel.grantAgreementNumber}`}</div>
                    </p>
                    <p>
                        Your agreement will now be reviewed and accepted by a representative from Austrade.
                        You can view your agreement anytime in the portal and you can also download your agreement as a PDF.
                    </p>
                    <br />
                    <div className="row">
                        <div className="col-sm-8 mb-1">
                            <Button className="emdg-btn emdg-btn--secondary mr-2" id="btnViewAgreement" onClick={() => history.goBack()}>
                                Back
                            </Button>
                        </div>
                    </div>
                </div>
            )
        }

        return (
            <div className='container-fluid pt-3'>
                <h2>Accept Grant Agreement</h2>
                <br />
                <ApplicationSummary
                    isConsultant={model.isConsultant}
                    isDraft={isDraft}
                    model={model}
                    applicationDetails={applicationDetails}
                    showGrantAgreement
                    grantAgreement={applicationDetails.grantAgreementSummaryModel}
                />
                <br />
                <div style={{ width: '80%' }}>
                    <h4>Declaration by an authorised person</h4>
                    <div style={{ marginTop: '1.5rem' }}>
                        I accept the terms and conditions of this grant agreement, acknowledge that I have the authority to enter into this agreement (including, for companies,
                        in accordance with section 126 of the Corporations Act 2001 (Cth)), and declare my identity is that of the MyID used to access this agreement.
                        <br />
                        <br />
                        I declare that
                        <ul className="mt-0">
                            <li>I have read and accept the <a href="https://www.austrade.gov.au/en/site-information/emdg-online-portal-terms-and-conditions" target="_blank">terms and conditions of use</a> and <a href="https://www.austrade.gov.au/en/site-information/emdg-online-portal-privacy-and-confidentiality" target="_blank">confidentiality and privacy provisions</a> prior to accepting the agreement</li>
                            <li>I am the primary contact who is entering into this grant agreement with the Commonwealth on behalf of the applicant business</li>
                            <li>I have a valid Australian Business Number (ABN)</li>
                            <li>I have not conducted business in an unprofessional or unethical manner</li>
                            <li>I am not currently under insolvency administration</li>
                            <li>I do not have any outstanding disqualifying convictions</li>
                            <li>I have complied with all my obligations under taxation laws during the current income year and the previous 2 income years</li>
                            <li>I am not included on the <i>National Redress Scheme’s list of Institutions</i> that have not joined or signified their intent to join the Scheme</li>
                            <li>the applicant does not currently have any outstanding debt with Austrade </li>
                            <li>I will remain subject to the eligibility provisions of the grant agreement and the EMDG Act, the EMDG Rules and Grant Guidelines
                                for the duration of your grant agreement period</li>
                            <li> I meet the following condition relevant to my tier:
                                <ul className="mt-0 ul-dash">
                                    <li>I am marketing an eligible product in a foreign country (All tiers); and</li>
                                    <li>I am ready to export eligible products and/or obtain training for the purpose of developing skills to market eligible products (Tier 1);</li>
                                    <li>I am exporting eligible products and seeking to expand my export promotion activity within existing markets (Tier 2);</li>
                                    <li>I am exporting eligible products, seeking to expand my export promotion activity and make a strategic shift in my export
                                        promotion activity in a new key market listed in Attachment 1 of the Grant Guidelines (Tier 3); or</li>
                                    <li>
                                        I am assisting SME members and undertake new export promotional activities and/or approved (by Austrade) export training
                                        activities (Rep body).
                                    </li>
                                </ul>


                            </li>

                        </ul>
                        I understand and accept that:
                        <ul className="mt-0">
                            <li>
                                by clicking ‘Accept’, this constitutes an electronic acceptance of the grant agreement, and this acceptance will be recorded in a
                                PDF version of the grant agreement
                            </li>
                            <li>this PDF will form a legally enforceable grant agreement in relation to the grant from the date it is received by the Commonwealth</li>
                            <li>during and at the conclusion of the grant period, Austrade may request evidence to confirm grant funds have been spent on grant activities</li>
                            <li>I may be asked to participate in the periodic evaluation of the services undertaken by Austrade</li>
                            <li>I may be contacted by Austrade to participate in case studies</li>
                            <li>I will acknowledge the EMDG grant on my business website</li>
                            <li>as a registered Australian business with an ABN, I can use the Australia Nation Brand logo alongside my own branding on signage,
                                publications and promotional activities when promoting my business’ (or SME members’) eligible products. Any use of the Australia’s
                                Nation Brand logo must be done in accordance with the Australia’s Nation Brand Guidelines, including all requirements in relation to sizing,
                                colouring, alignment and placement
                            </li>
                            <li>Austrade may share my contact details with other government bodies and third parties for trade facilitation or business support purposes.</li>
                        </ul>
                    </div>

                    <br />

                    <Checkbox
                        ref={(r) => { this.declaration = r }}
                        id="declaration"
                        required
                        onChange={(grantAgreementAccepted) => this.onChange({ grantAgreementAccepted })}
                        value={grantAgreementModel.grantAgreementAccepted}
                        label="By marking this box I agree to all of the above declarations and confirm all of the above statements to be true"
                    />
                    <NumericSelectField
                        ref={(r) => { this.title = r }}
                        id="title"
                        label="Applicant Title"
                        value={grantAgreementModel.title}
                        onChange={(title) => this.onChange({ title })}
                        dataSource={salutationDataSource}
                        block={false}
                        required
                    />

                    <TextField
                        ref={(r) => { this.givenName = r }}
                        id="givenName"
                        label="Applicant Given name"
                        value={grantAgreementModel.givenName}
                        onChange={(givenName) => this.onChange({ givenName: givenName.trim() })}
                        required
                        maxLength={50}
                        errorMessage={this.getErrorMessage('givenName')}
                    />

                    <TextField
                        ref={(r) => { this.familyName = r }}
                        id="familyName"
                        label="Applicant Family name"
                        value={grantAgreementModel.familyName}
                        onChange={(familyName) => this.onChange({ familyName: familyName.trim() })}
                        required
                        maxLength={50}
                        errorMessage={this.getErrorMessage('familyName')}
                    />

                    <TextField
                        ref={(r) => { this.position = r }}
                        id="position"
                        label="Applicant Position Title"
                        value={grantAgreementModel.position}
                        onChange={(position) => this.onChange({ position })}
                        required
                        maxLength={50}
                    />
                    <br />
                    <div className="row">
                        <div className="col-sm-8 mb-1">
                            <Button className="emdg-btn emdg-btn--secondary mr-2" id="btnViewAgreement" onClick={() => history.goBack()} >
                                Back
                            </Button>
                            <Button className='emdg-btn emdg-btn--secondary' id="btnDeclineAgreement" onClick={() => this.downloadAgreement()} >
                                View Grant Agreement
                            </Button>

                        </div>
                        <div className="col-sm-4 mb-1">
                            <div className="float-sm-right">
                                <Button className='emdg-btn'  disabled={disableSubmit} id="btnAcceptAgreement" onClick={() => this.validate()}>
                                    Accept and Submit
                                </Button>
                            </div>
                        </div>
                    </div>
                </div>
                <Backdrop on={loading} />
            </div>
        )
    }
}
